import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useSubscription } from '../context/SubscriptionContext';
import { addWeeks, subWeeks, startOfWeek, addDays, format, isSameDay } from 'date-fns';
import { nb } from 'date-fns/locale';

const BookDemo = () => {
    const navigate = useNavigate();
    const { addDemoBooking } = useSubscription();
    
    const [step, setStep] = useState(1);
    
    // Dynamic Calendar State
    const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    
    const [formData, setFormData] = useState({
        company: '',
        contact: '',
        email: '',
        phone: ''
    });

    // Generate Mon-Fri for the currently selected week
    const weekDays = useMemo(() => {
        return Array.from({ length: 5 }).map((_, i) => addDays(currentWeekStart, i));
    }, [currentWeekStart]);

    const handlePrevWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
    const handleNextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));

    const times = ['09:00', '09:30', '10:00', '10:30', '11:00', '13:00', '13:30', '14:00', '14:30', '15:00'];

    const handleSubmit = (e) => {
        e.preventDefault();
        addDemoBooking({
            ...formData,
            date: format(selectedDate, 'yyyy-MM-dd'),
            time: selectedTime
        });
        setStep(3); // Success step
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] font-sans flex flex-col">
            {/* Header */}
            <header className="py-6 px-8 flex justify-between items-center bg-white border-b border-slate-100">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900">
                    <SafeIcon icon="⬅️" className="w-4 h-4" /> Tilbake
                </button>
                <div className="flex items-center gap-2 font-black text-xl text-slate-900">
                    <SafeIcon icon="🛡️" className="text-blue-600" /> SalesWork
                </div>
                <div className="w-20"></div> {/* Spacer */}
            </header>

            <main className="flex-1 flex flex-col items-center py-12 px-4">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-black text-slate-900 mb-4">Se plattformen i aksjon</h1>
                    <p className="text-slate-500 font-medium max-w-lg mx-auto">
                        Bestill en uforpliktende gjennomgang med en av våre eksperter. Vi viser deg hvordan systemet løser dine behov.
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col md:flex-row max-w-5xl w-full">
                    
                    {/* Left Panel (Dark) */}
                    <div className="bg-[#111827] text-white p-8 md:w-1/3 flex flex-col">
                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                            <SafeIcon icon="🎥" className="text-xl" />
                        </div>
                        <h2 className="text-2xl font-black mb-2">Book en demo</h2>
                        <p className="text-slate-400 text-sm mb-8">Se hvordan plattformen kan forenkle din hverdag.</p>
                        
                        <div className="mt-auto space-y-6">
                            <div>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Tjeneste</p>
                                <p className="font-bold">Plattform-demo (Video)</p>
                                <p className="text-xs text-slate-400 mt-1">En personlig gjennomgang av SalesWork CRM med en av våre eksperter.</p>
                            </div>

                            <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                                <p className="text-xs font-bold flex items-center gap-2 mb-2">
                                    <SafeIcon icon="ℹ️" /> Hva går vi gjennom?
                                </p>
                                <p className="text-[11px] text-slate-400 leading-relaxed">
                                    Vi går gjennom hvordan systemet fungerer, viser salgsmodulen for ansatte, og demonstrerer oppfølging i admin-panelet. Vi svarer også på dine spørsmål. Møtet er helt uforpliktende.
                                </p>
                            </div>

                            <div>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Varighet</p>
                                <p className="font-bold">30 min</p>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel (Content) */}
                    <div className="p-8 md:p-12 md:w-2/3">
                        {step === 1 && (
                            <div className="animate-in fade-in">
                                <div className="flex justify-between items-center mb-8">
                                    <h3 className="text-xl font-black text-slate-900">Velg tidspunkt</h3>
                                    <div className="flex items-center gap-4 text-sm font-bold text-slate-700">
                                        <button onClick={handlePrevWeek} className="p-1 hover:bg-slate-100 rounded"><SafeIcon icon="<" /></button>
                                        <span className="capitalize w-28 text-center">{format(currentWeekStart, 'MMMM yyyy', { locale: nb })}</span>
                                        <button onClick={handleNextWeek} className="p-1 hover:bg-slate-100 rounded"><SafeIcon icon=">" /></button>
                                    </div>
                                </div>

                                {/* Dynamic Calendar Days */}
                                <div className="flex justify-between mb-8">
                                    {weekDays.map((date, i) => {
                                        const isSelected = selectedDate && isSameDay(date, selectedDate);
                                        return (
                                            <div 
                                                key={i} 
                                                onClick={() => { setSelectedDate(date); setSelectedTime(null); }}
                                                className={`flex flex-col items-center justify-center w-14 h-16 cursor-pointer rounded-xl transition-all ${isSelected ? 'bg-blue-50 text-blue-700 scale-110 shadow-sm border border-blue-200' : 'hover:bg-slate-50 text-slate-600 border border-transparent hover:border-slate-200'}`}
                                            >
                                                <span className="text-[10px] font-black uppercase tracking-widest mb-1">{format(date, 'E', { locale: nb })}</span>
                                                <span className="text-xl font-black">{format(date, 'd')}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Time Slots (Only visible if date is selected) */}
                                <div className="min-h-[220px]">
                                    {selectedDate ? (
                                        <div className="animate-in fade-in slide-in-from-top-4">
                                            <p className="text-sm font-medium text-slate-500 mb-4 border-t border-slate-100 pt-4">
                                                Ledige timer for {format(selectedDate, 'EEEE d. MMMM', { locale: nb })}
                                            </p>
                                            <div className="grid grid-cols-3 gap-3 mb-8">
                                                {times.map(t => (
                                                    <button 
                                                        key={t}
                                                        onClick={() => setSelectedTime(t)}
                                                        className={`py-3 rounded-lg border text-sm font-bold transition-all ${selectedTime === t ? 'border-blue-600 bg-blue-600 text-white shadow-md' : 'border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50/30'}`}
                                                    >
                                                        {t}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center h-full pt-8 text-slate-400 border-t border-slate-100 border-dashed">
                                            <SafeIcon icon="👆" className="text-3xl mb-2 opacity-50" />
                                            <p className="text-sm font-medium">Velg en dato ovenfor for å se ledige tider.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end pt-6 border-t border-slate-100">
                                    <button 
                                        disabled={!selectedTime}
                                        onClick={() => setStep(2)}
                                        className="px-8 py-3 bg-blue-600 text-white rounded-lg font-bold disabled:opacity-50 hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-lg disabled:shadow-none"
                                    >
                                        Neste steg <SafeIcon icon=">" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <form onSubmit={handleSubmit} className="animate-in fade-in slide-in-from-right-4 h-full flex flex-col">
                                <div className="flex items-center gap-4 mb-8">
                                    <button type="button" onClick={() => setStep(1)} className="p-2 hover:bg-slate-100 rounded-full">
                                        <SafeIcon icon="⬅️" />
                                    </button>
                                    <h3 className="text-xl font-black text-slate-900">Dine detaljer</h3>
                                </div>

                                <div className="space-y-4 flex-1">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Bedrift</label>
                                        <input required className="w-full p-3 border border-slate-200 rounded-lg outline-none focus:border-blue-500" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Ditt Navn</label>
                                        <input required className="w-full p-3 border border-slate-200 rounded-lg outline-none focus:border-blue-500" value={formData.contact} onChange={e => setFormData({...formData, contact: e.target.value})} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">E-post</label>
                                            <input required type="email" className="w-full p-3 border border-slate-200 rounded-lg outline-none focus:border-blue-500" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Telefon</label>
                                            <input required type="tel" className="w-full p-3 border border-slate-200 rounded-lg outline-none focus:border-blue-500" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-6 border-t border-slate-100 mt-6">
                                    <button type="submit" className="px-8 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-lg">
                                        Bekreft Booking
                                    </button>
                                </div>
                            </form>
                        )}

                        {step === 3 && (
                            <div className="h-full flex flex-col items-center justify-center text-center animate-in zoom-in">
                                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-500 text-3xl mb-6">
                                    <SafeIcon icon="✅" />
                                </div>
                                <h3 className="text-2xl font-black text-slate-900 mb-2">Møtet er bekreftet!</h3>
                                <p className="text-slate-500 mb-8 max-w-sm">
                                    Vi har sendt en kalenderinvitasjon til {formData.email} for {format(selectedDate, 'do MMMM', { locale: nb })} kl {selectedTime}.
                                </p>
                                <Link to="/" className="px-6 py-3 bg-slate-100 text-slate-700 font-bold rounded-lg hover:bg-slate-200 transition-colors">
                                    Tilbake til forsiden
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
                
                <p className="text-xs text-slate-400 mt-8">Møtet foregår via Google Meet eller Teams. Du trenger ikke installere noe.</p>
            </main>
        </div>
    );
};

export default BookDemo;